import dayjs, { Dayjs } from "dayjs";
import { ReceptionFractionWindow, ReceptionWindowGranularity } from "@/types";

/** Most-specific-first, per handoff doc §9 precedence rule. */
const GRANULARITY_PRECEDENCE: ReceptionWindowGranularity[] = ["WEEK", "MONTH", "YEAR"];

function windowCoversDate(window: ReceptionFractionWindow, date: Dayjs): boolean {
  const start = dayjs(window.periodStart);
  switch (window.granularity) {
    case "WEEK":
      return !date.isBefore(start, "day") && date.isBefore(start.add(7, "day"), "day");
    case "MONTH":
      return date.isSame(start, "month");
    case "YEAR":
      return date.isSame(start, "year");
    default:
      return false;
  }
}

/**
 * FE-only display derivation of "which fraction window applies to this date" — mirrors the
 * backend's WEEK > MONTH > YEAR precedence (handoff §9) purely for calendar labelling. Never
 * used to enforce or validate a reservation; the backend re-derives this itself.
 */
export function resolveActiveFractionWindow(
  dateStr: string,
  windows: ReceptionFractionWindow[]
): ReceptionFractionWindow | undefined {
  const date = dayjs(dateStr);
  for (const granularity of GRANULARITY_PRECEDENCE) {
    const match = windows.find((w) => w.granularity === granularity && windowCoversDate(w, date));
    if (match) return match;
  }
  return undefined;
}

/** Effective accepted-fraction names for display. Empty for closed/acceptAll/unmatched windows. */
export function resolveAcceptedFractionNames(
  window: ReceptionFractionWindow | undefined,
  fractionNameMap: Map<number, string>
): string[] {
  if (!window || window.closed || window.acceptAll) return [];
  return window.fractionIds.map((id) => fractionNameMap.get(id) ?? String(id));
}

/** Whether a resolved window accepts a specific fraction — display-only, see note above. */
export function windowAcceptsFraction(window: ReceptionFractionWindow | undefined, fractionId: number | null): boolean {
  if (!window) return true;
  if (window.closed || fractionId == null) return false;
  if (window.acceptAll) return true;
  return window.fractionIds.includes(fractionId);
}
